import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {catchError, forkJoin, of} from 'rxjs';
import {BusinessTypeSelectModalComponent} from './add/business-type-select-modal';
import {BusinessType} from './add/add-business-form-data';
import {BusinessApiService} from './add/business-api.service';
import {UserBusinessApiService} from './user-business-api.service';
import {BusinessTypeDto, UserBusinessDto} from '../../../models/generated-models';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {firstNonBlank} from '../../shared/commons/text-utils';
import {TradeStore} from '../trade/trade.store';

export interface Business {
  id: string;
  number: string;
  name: string;
  subtitle: string;
  type: BusinessType;
  /** ประเภทธุรกิจ titles resolved from UserBusinessDto.businessTypeIds. */
  businessTypeTitles: string[];
  address: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  savedBy: string;
  savedDate: string;
  status: 'active' | 'inactive';
  imageUrl?: string;
  source: UserBusinessDto;
}

export type BusinessFilter = 'all' | 'active' | 'inactive' | 'juristic';

@Component({
  selector: 'app-user-business',
  imports: [
    CommonModule,
    FormsModule,
    PageTitleComponent
  ],
  templateUrl: './user-business.html',
  styleUrl: './user-business.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserBusiness implements OnInit {
  private readonly store = inject(TradeStore);
  /**
   * Acting under a grant: the backend lists the grantor's businesses and refuses writes, so
   * the page shows them read-only with a ดำเนินการแทน badge and no add/edit controls — the
   * switcher, not this page, decides whose businesses are on screen.
   */
  readonly actingFor = computed(() => this.store.activeRoleAssignment());
  readonly actingForName = computed(() => {
    const grant = this.actingFor();
    return grant ? grant.grantorName || grant.grantorTaxId || 'ผู้มอบสิทธิ์' : '';
  });

  searchTerm = signal('');
  statusFilter = signal<BusinessFilter>('all');
  readonly filterOptions: Array<{value: BusinessFilter; label: string}> = [
    {value: 'all', label: 'ทั้งหมด'},
    {value: 'active', label: 'ใช้งาน'},
    {value: 'juristic', label: 'นิติบุคคล'},
    {value: 'inactive', label: 'ไม่ใช้งาน'}
  ];
  allBusinesses = signal<Business[]>([]);
  businesses = signal<Business[]>([]);
  isLoading = signal<boolean>(false);
  private readonly failedAvatars = signal<Set<Business['id']>>(new Set());
  /** business-type id -> title, loaded once alongside the business list. */
  private businessTypeTitles = new Map<number, string>();

  hasAvatar(business: Business): boolean {
    return !!business.imageUrl && !this.failedAvatars().has(business.id);
  }

  onAvatarError(business: Business): void {
    this.failedAvatars.update((set) => {
      const next = new Set(set);
      next.add(business.id);
      return next;
    });
  }

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userBusinessApiService: UserBusinessApiService,
    private businessApiService: BusinessApiService,
    private toast: ToastService
  ) {
  }

  ngOnInit(): void {
    // Whose businesses the backend lists depends on the act-as state it holds; wait for the
    // client's copy of that state so the badge and the list agree from the first paint.
    this.isLoading.set(true);
    void this.store.whenActAsRestored().then(() => this.loadBusinesses());
  }

  private loadBusinesses(): void {
    this.isLoading.set(true);
    // The type titles are only decoration on the cards, so a failing lookup
    // must not hide the list itself.
    forkJoin({
      // switched-off types too, so a business that still carries one keeps its title chip
      businessTypes: this.businessApiService.getBusinessTypes(true)
        .pipe(catchError(() => of([] as BusinessTypeDto[]))),
      businesses: this.userBusinessApiService.listBusinesses()
    }).subscribe({
      next: ({businessTypes, businesses}) => {
        this.businessTypeTitles = new Map(
          (businessTypes ?? [])
            .filter((item) => item?.id != null && !!`${item.title ?? ''}`.trim())
            .map((item) => [item.id as number, `${item.title}`.trim()] as const)
        );
        const mapped = businesses.map((item) => this.mapSavedBusiness(item));
        this.allBusinesses.set(mapped);
        this.applyFilters();
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load businesses', error);
        this.isLoading.set(false);
      }
    });
  }

  private resolveBusinessTypeTitles(ids: number[] | null | undefined): string[] {
    if (!Array.isArray(ids)) {
      return [];
    }
    return Array.from(new Set(
      ids
        .map((id) => this.businessTypeTitles.get(Number(id)) ?? '')
        .filter((title) => !!title)
    ));
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(BusinessTypeSelectModalComponent, {
      size: 'md',
      centered: true
    });
    modalRef.result
      .then((businessType: BusinessType) => {
        this.router.navigate(['add'], {
          relativeTo: this.activatedRoute,
          state: { businessType }
        });
      })
      .catch(() => undefined);
  }

  openEditModal(business: Business): void {
    this.router.navigate(['edit'], {
      relativeTo: this.activatedRoute,
      state: { editMode: true, business: business.source }
    });
  }

  searchBusiness(): void {
    this.applyFilters();
  }

  onSearchTermChange(value: string): void {
    this.searchTerm.set(value);
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm.set('');
    this.applyFilters();
  }

  clearFilters(): void {
    this.searchTerm.set('');
    this.statusFilter.set('all');
    this.applyFilters();
  }

  setFilter(filter: BusinessFilter): void {
    this.statusFilter.set(this.statusFilter() === filter ? 'all' : filter);
    this.applyFilters();
  }

  isJuristicBusiness(business: Business): boolean {
    return business.type === 'company' || business.type === 'cooperative';
  }

  countForFilter(filter: BusinessFilter): number {
    const source = this.allBusinesses();
    switch (filter) {
      case 'active':
        return source.filter((business) => business.status === 'active').length;
      case 'inactive':
        return source.filter((business) => business.status === 'inactive').length;
      case 'juristic':
        return source.filter((business) => this.isJuristicBusiness(business)).length;
      default:
        return source.length;
    }
  }

  private applyFilters(source = this.allBusinesses()): void {
    this.businesses.set(this.filterBusinesses(source, this.searchTerm(), this.statusFilter()));
  }

  private filterBusinesses(source: Business[], search: string, filter: BusinessFilter): Business[] {
    const term = search.trim().toLowerCase();
    return source.filter((business) =>
      this.matchesFilter(business, filter) &&
      (!term || [
        business.number,
        business.name,
        business.address,
        business.phone,
        business.fax,
        business.email,
        business.website,
        business.savedBy
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)))
    );
  }

  private matchesFilter(business: Business, filter: BusinessFilter): boolean {
    switch (filter) {
      case 'active':
        return business.status === 'active';
      case 'inactive':
        return business.status === 'inactive';
      case 'juristic':
        return this.isJuristicBusiness(business);
      default:
        return true;
    }
  }

  private mapSavedBusiness(saved: UserBusinessDto): Business {
    const type: BusinessType = saved.businessFormat === 2
      ? 'company'
      : saved.businessFormat === 3
        ? 'cooperative'
        : 'individual';
    const subtitle = type === 'company'
      ? '(นิติบุคคล)'
      : type === 'cooperative'
        ? (saved.juristicType === 8 ? '(วิสาหกิจชุมชน)' : '(สหกรณ์)')
        : '(บุคคลธรรมดา)';

    return {
      id: `${saved.id ?? Date.now()}`,
      number: saved.taxId ?? '-',
      name: saved.title ?? '-',
      subtitle,
      type,
      businessTypeTitles: this.resolveBusinessTypeTitles(saved.businessTypeIds),
      address: saved.addressNo ?? saved.hqAddressNo ?? '-',
      phone: firstNonBlank(saved.phone, saved.hqPhone, '-'),
      fax: firstNonBlank(saved.fax, saved.hqFax, '-'),
      email: saved.email ?? '-',
      website: saved.webSite ?? '-',
      savedBy: saved.savedBy ?? 'ระบบ',
      savedDate: saved.savedDate ?? new Date().toLocaleDateString('th-TH'),
      status: saved.state === 0 ? 'inactive' : 'active',
      imageUrl: saved.businessRegFile ?? undefined,
      source: saved
    };
  }
}
